import { useMemo, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import Edit from "../../../assets/EditInWhite.svg";
import BoxComponent from "../../../components/CommonComponents/BoxComponent/BoxComponent";
import Paginator, {
  PaginatorForRDT,
} from "../../../components/CommonComponents/Paginator/Paginator";
import SortIcon from "../../../components/CommonComponents/SortIcon/SortIcon";
import { tableCustomStyles } from "../../../components/CommonComponents/Table/Table";
import { useDebounce } from "../../../hooks/common";
import {
  TFormConfig,
  useFormConfigCount,
  useFormConfigs,
} from "../../../hooks/form-step-config.hooks";
import { formatDate } from "../../../utils/date";
import EditButton, { ViewButton } from "../../../components/CommonComponents/EditComponent/EditComponent";

type ActionType = {
  type: null | "edit";
  field?: TFormConfig;
};

export default function StepConfigurations() {
  const limit = 10;
  const [view, setView] = useState<"table" | "card">("table");

  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [{ sortKey, sortOrder }, setSort] = useState({
    sortKey: "",
    sortOrder: 1,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const search = useDebounce(searchQuery);
  const { isLoading: isLoading1, data: count } = useFormConfigCount({ search });
  const { isLoading: isLoading2, data } = useFormConfigs({
    search,
    page: page,
    limit: limit,
    sort: sortKey,
    sortVal: sortOrder,
  });

  const columns = useMemo(
    (): TableColumn<TFormConfig>[] => [
      {
        name: "Sr. No",
        // selector: (_, i) => (page - 1) * limit + (i ?? 0) + 1,
        selector: (_, i) => {
          const serialNumber = (page - 1) * limit + (i ?? 0) + 1;
          return serialNumber.toString().padStart(3, '0');
        },
        grow: 0.5,
        center: true,
      },
      {
        name: "Name",
        selector: (d) => d.serviceName,
        center: true,
        sortable: true,
        sortField: "serviceName",
        grow: 3,
      },
      {
        name: "Last Published Date",
        sortable: true,
        grow: 1,
        sortField: "updatedAt",
        selector: (d) => formatDate(d.updatedAt),
        center: true,
        width: "230px",
        sortFunction: (a: any, b: any) => {
          const dateA = new Date(a.updated_date);
          const dateB = new Date(b.updated_date);
          return dateA.getTime() - dateB.getTime();
        }
      },
      {
        name: "Status",
        center: true,
        width: "160px",
        sortable: true,
        sortField: "active",
        cell: (d) => (
          <div
            className={`${d.active ? "activeButtonCss" : "inactiveButtonCss"
              // } inline-block py-1 px-4 md:px-6 shrink-0 rounded-full text-white text-xs`}
              } flex justify-center items-center`}
          >
            {d?.active ? "Active" : "Inactive"}
          </div>
        ),
      },
      {
        name: 'Created By',
        selector: (row: any) => row.createdBy,
        cell: (row: any) => <span className="text-center">{row.createdBy}</span>,
        center: true,
        sortable: true,
        width: "170px",
      },
      {
        name: 'Created On',
        selector: (row: any) => formatDate(new Date(row.createdAt)),
        center: true,
        sortable: true,
        width: "170px",
      },
      {
        name: 'Modified By',
        selector: (row: any) => row.modifiedBy,
        cell: (row: any) => <span className="text-center">{row.modifiedBy}</span>,
        center: true,
        sortable: true,
        width: "170px",
      },
      {
        name: 'Modified On',
        selector: (row: any) => formatDate(new Date(row.updatedAt)),
        center: true,
        sortable: true,
        width: "170px",
      },
      {
        name: "Action",
        center: true,
        grow: 0.5,
        cell: (d) => (
          // <img
          //   src={Edit}
          //   alt="Edit"
          //   onClick={() => navigate(`/edit-step-configuration/${d.id}`)}
          //   className="globalEditCss"
          // />
          <div className="flex space-x-2">
            <EditButton
              onClick={() => navigate(`/edit-step-configuration/${d.id}`)}
            />
            <ViewButton
              disabled={true}
              onClick={function (): void {
                throw new Error("Function not implemented.");
              }}
            />
          </div>
        ),
      },
    ],
    [navigate, page, limit]
  );

  return (
    <div className="p-4 md:p-8 overflow-auto">
      <div className="flex-wrap md:flex-nowrap gap-2 flex justify-between mb-2">
        <h4 className="text-2xl font-bold">Form Configuration</h4>
        <div className="space-x-2 md:space-x-4">
          <button
            className="py-2 px-6 font-semibold bg-blue-600 text-white rounded-lg"
            onClick={() => navigate("/add-step-configuration")}
          >
            Create
          </button>
          {/* <button
            className="py-2 px-6 font-semibold bg-blue-600 text-white rounded-lg"
            onClick={() => navigate("/add-form-configuration")}
          >
            Create Form
          </button> */}
        </div>
      </div>
      <div className="flex-wrap gap-4 md:flex-nowrap flex flex-col-reverse md:flex-row justify-between md:items-center">
        <div className="shrink-0">
          <button
            className="p-2 md:p-4 font-semibold text-base md:text-lg rounded-t-2xl bg-gray-200 border-b-2 border-gray-200"
            onClick={() => navigate("/form-configurations")}
          >
            Form Configuration
          </button>
          <button className="p-2 md:p-4 font-semibold text-base md:text-lg rounded-t-2xl bg-white border-b-2 border-primary">
            Step Configuration
          </button>
        </div>
        <div className="flex items-center space-x-4 grow md:grow-0">
          <button
            className="p-3 bg-white border border-[#968f8f] rounded-xl flex justify-center items-center"
            onClick={() => setView((e) => (e === "card" ? "table" : "card"))}
          >
            {view === "table" ? (
              <i className="fa fa-th text-primary m-0" />
            ) : (
              <i className="fa fa-list-ul text-primary m-0" />
            )}
          </button>
          <div className="flex rounded-xl border border-[#968f8f] bg-white items-stretch overflow-hidden grow md:grow-0">
            <input
              type="text"
              placeholder="Search..."
              className="bg-none px-3"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="p-3 shadow-md flex justify-center items-center">
              <i className="fa fa-search text-primary m-0" />
            </div>
          </div>
        </div>
      </div>
      {view === "table" ? (
        <BoxComponent>
          <DataTable
            pagination
            paginationServer
            columns={columns}
            className="mt-4 rounded-0"
            sortIcon={<SortIcon />}
            data={data?.data || []}
            onChangePage={(p) => setPage(p)}
            customStyles={tableCustomStyles}
            paginationTotalRows={count?.data || 0}
            progressPending={isLoading1 || isLoading2}
            onSort={(col, dir) => {
              setSort({
                sortKey: col.sortField || "",
                sortOrder: dir === "asc" ? 1 : -1,
              });
            }}
            paginationComponent={PaginatorForRDT}
          />
        </BoxComponent>
      ) : (
        <>
          <BoxComponent>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {(data?.data || []).map((e) => (
                <div
                  className="p-4 rounded-2xl bg-white flex flex-col shadow-lg border border-gray-300"
                  key={e.id}
                >
                  <div className="flex justify-between items-center space-x-4 mb-4">
                    <div
                      className={`${e.active ? "activeButtonCss" : "inactiveButtonCss"
                        // } inline-block py-1 px-4 rounded-full text-white text-xs`}
                        } flex justify-center items-center`}
                    >
                      {e?.active ? "Active" : "Inactive"}
                    </div>
                    {/* <img
                      src={Edit}
                      alt="Edit"
                      onClick={() =>
                        navigate(`/edit-step-configuration/${e.id}`)
                      }
                      className="globalEditCss"
                    /> */}
                    <div className="flex space-x-2">
                      <EditButton
                        onClick={() =>
                          navigate(`/edit-step-configuration/${e.id}`)
                        }
                      />
                      <ViewButton
                        disabled={true}
                        onClick={function (): void {
                          throw new Error("Function not implemented.");
                        }}
                      />
                    </div>
                  </div>
                  <div className="mb-2 flex text-[12px]">
                    <p className="font-bold text-base w-1/2">Name</p>
                    <p className="text-base mx-2">:</p>
                    <p className="text-base w-2/3">{e.serviceName}</p>
                  </div>
                  <div className="mb-2 flex text-[12px]">
                    <p className="font-bold text-base w-1/2">
                      Last Published Date
                    </p>
                    <p className="text-base mx-2">:</p>
                    <p className="text-base w-2/3">{formatDate(e.updatedAt)}</p>
                  </div>
                </div>
              ))}
            </div>
            <Paginator
              limit={10}
              page={page}
              setPage={setPage}
              count={count?.data || 0}
            />
          </BoxComponent>
        </>
      )}
    </div>
  );
}
